import { useState, useEffect } from 'react';
import './PaperCalendar.css';
import { useLang } from '../context/LanguageContext';

import { API_URL as API } from '../config/env.js';
function toKey(y, m, d) {
  return `${y}-${String(m + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
}

export default function PaperCalendar({ onSelectDate, onClose }) {
  const { t } = useLang();
  const today = new Date();
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [hovered,   setHovered]   = useState(null);
  const [availableDates, setAvailableDates] = useState(new Set());

  useEffect(() => {
    fetch(`${API}/api/papers`)
      .then(r => r.json())
      .then(d => setAvailableDates(new Set(d.dates || [])))
      .catch(() => {});
  }, []);

  const firstDay    = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const recentDates = [...availableDates].sort().reverse().slice(0, 4);
  const todayKey = toKey(today.getFullYear(), today.getMonth(), today.getDate());

  const formatDisplay = (dateKey) => {
    const [y, m, d] = dateKey.split('-');
    return `${parseInt(d)} ${t.months[parseInt(m) - 1]} ${y}`;
  };

  return (
    <div className="cal-backdrop" onClick={onClose}>
      <div className="cal-shell" onClick={e => e.stopPropagation()}>

        {/* ── Left Panel ── */}
        <div className="cal-left">
          <button type="button" className="cal-x" onClick={onClose} aria-label={t.closeBtn}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" aria-hidden>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div className="cal-left-top">
            <img src="/rk-logo.webp" alt="" className="cal-logo" />
            <h2 className="cal-left-title">{t.calTitle}</h2>
            <p className="cal-left-sub">{t.calSub}</p>
          </div>

          <div className="cal-month-nav">
            <button className="cal-nav-btn" onClick={prevMonth}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div className="cal-month-display">
              <span className="cal-month-name">{t.months[viewMonth]}</span>
              <span className="cal-year-name">{viewYear}</span>
            </div>
            <button className="cal-nav-btn" onClick={nextMonth}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>

          <div className="cal-recent">
            <span className="cal-recent-label">{t.calRecent}</span>
            {recentDates.length === 0 && (
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem' }}>—</span>
            )}
            {recentDates.map(dk => (
              <button key={dk} className="cal-recent-item" onClick={() => onSelectDate(dk)}>
                <span className="cal-recent-dot" />
                {formatDisplay(dk)}
              </button>
            ))}
          </div>
        </div>

        {/* ── Right Panel ── */}
        <div className="cal-right">
          <div className="cal-day-row">
            {(t.calDays || []).map(d => <div key={d} className="cal-dname">{d}</div>)}
          </div>

          <div className="cal-grid">
            {cells.map((day, i) => {
              if (!day) return <div key={`e-${i}`} className="cal-empty" />;
              const key      = toKey(viewYear, viewMonth, day);
              const hasPaper = availableDates.has(key);
              const isToday  = key === todayKey;
              const isHov    = hovered === key;
              return (
                <button
                  key={key}
                  className={[
                    'cal-cell',
                    hasPaper          ? 'has-paper'  : '',
                    isToday           ? 'is-today'   : '',
                    isHov && hasPaper ? 'is-hovered' : '',
                  ].join(' ')}
                  disabled={!hasPaper}
                  onClick={() => hasPaper && onSelectDate(key)}
                  onMouseEnter={() => setHovered(key)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <span className="cal-num">{day}</span>
                  {hasPaper && <span className="cal-pip" />}
                </button>
              );
            })}
          </div>

          <div className="cal-footer">
            <span className="cal-legend-item">
              <span className="cal-pip-sample" /> {t.calAvailable}
            </span>
            <span className="cal-legend-item muted">
              <span className="cal-today-sample" /> {t.calToday}
            </span>
          </div>
        </div>

      </div>
    </div>
  );
}
